// OgsDirectUpload — multi-file uploader for a character's OGS inference bundle.
//
// Launched from the content browser "New → OGS bundle" menu (not a file drop —
// the bundle's .onnx/.json/.bin/.npy extensions collide with other kinds). The
// user picks the bundle files; each is POSTed to /characters/{slug}/ogs (the
// loose-file collection the OGS render path reads). Uploads run sequentially so
// per-file progress is legible. Loud failure per CLAUDE.md UX rules.

import React, { useRef, useState } from 'react'
import WizardShell, { tokens } from './WizardShell'

// Inline <code> spans in the copy below — body text color, not a second palette.
const CODE = { color: tokens.text }
import { CustomAssetManager } from '../../../lib/CustomAssetManager'

export default function OgsDirectUpload({ onDone, onCancel }) {
  const inputRef = useRef(null)
  const [items, setItems] = useState([]) // { name, status, pct }
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const pick = () => inputRef.current?.click()

  const patch = (i, fields) =>
    setItems(s => s.map((it, idx) => (idx === i ? { ...it, ...fields } : it)))

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || [])
    if (!files.length) return
    setError(null)
    setBusy(true)
    setItems(files.map(f => ({ name: f.name, status: 'pending', pct: 0 })))
    for (let i = 0; i < files.length; i++) {
      patch(i, { status: 'uploading' })
      try {
        await CustomAssetManager.uploadOgsAsset(files[i], {
          onProgress: (p) => patch(i, { pct: p.fraction >= 0 ? Math.round(p.fraction * 100) : -1 }),
        })
        patch(i, { status: 'done', pct: 100 })
      } catch (e) {
        patch(i, { status: 'error' })
        setError(`Failed to upload ${files[i].name}: ${e?.message ?? e}`)
        setBusy(false)
        return // stop on first error — loud, no silent partial success
      }
    }
    setBusy(false)
    onDone?.()
  }

  const submitLabel = busy ? 'Uploading…' : (error ? 'Close' : (items.length ? 'Choose more…' : 'Choose files…'))

  return (
    <WizardShell
      title={error ? 'OGS upload failed' : 'Upload OGS bundle'}
      subtitle="Inference files — geom / appr / mesh / rig / manifest"
      onCancel={onCancel}
      onSubmit={error ? onCancel : pick}
      submitDisabled={busy}
      submitLabel={submitLabel}
      error={error}
    >
      <input ref={inputRef} type="file" multiple style={{ display: 'none' }}
        onChange={e => { onFiles(e.target.files); e.target.value = '' }} />
      <div style={{ padding: '4px 0' }}>
        <div style={{ fontSize: 11, color: tokens.dim, lineHeight: 1.5, marginBottom: 12 }}>
          Pick this character's OGS inference files — e.g. <code style={CODE}>geom.onnx</code>,{' '}
          <code style={CODE}>appr.onnx</code>, <code style={CODE}>mesh.json</code>,{' '}
          <code style={CODE}>mesh.bin</code>, <code style={CODE}>rig_names.json</code>,{' '}
          <code style={CODE}>export_manifest.json</code>.
          Each uploads to this character's <code style={CODE}>/ogs</code> collection.
        </div>
        {items.length === 0 ? (
          <button
            onClick={pick}
            style={{
              width: '100%', padding: '10px', borderRadius: 6, cursor: 'pointer',
              border: `1px dashed ${tokens.primary}`, background: tokens.bgInset, color: tokens.text,
              fontSize: 12,
            }}
          >
            Choose files…
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {items.map((it, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 10,
                color: it.status === 'error' ? tokens.danger : (it.status === 'done' ? tokens.primary : tokens.text),
              }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</span>
                <span>{it.status === 'done' ? '✓' : it.status === 'error' ? '✕' : (it.pct >= 0 ? `${it.pct}%` : '…')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </WizardShell>
  )
}
